import React from "react";
import { shallowEqual, useDispatch } from "react-redux";

import useTheme from "@material-ui/core/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import TableHeader from "components/VetExamsTable/TableHeader";
import ActionColumnHeaders from "components/VetExamsTable/TableHeader/ActionColumnHeaders/ActionColumnHeaders";
import { getSortDirection } from "components/VetExamsTable/TableHeader/helper";
import {
  ERowColumn,
  ITableHeaderColumnsContent
} from "components/VetExamsTable/types";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { SortOrder } from "interfaces/SortOrder";
import { setOrderBy, setSortOrder } from "store/actions/preRaceScreeningPage";
import { checkCurrentRaceEntriesTableTab } from "../../helper";
import { EPreRaceTableSortingField } from "../../types";

interface IProps {
  columnsClassName: string;
  columns: ERowColumn[];
  refElement: HTMLElement;
}

const ENTRIES_TAB_ACTION_COLUMNS = ["Exam", "Pass"];
const ASSIGN_ACTION_COLUMNS = ["Assign"];

const PreRacesTableHeader = (props: IProps) => {
  const { columns, columnsClassName, refElement } = props;

  const dispatch = useDispatch();
  const theme = useTheme();
  const matchesDownSx = useMediaQuery(theme.breakpoints.down("sx"), {
    noSsr: true
  });

  const { selectedTab, orderBy, order } = useAppSelector(
    state => ({
      selectedTab: state.preRaceScreeningPage.selectedTab,
      orderBy: state.preRaceScreeningPage.orderBy,
      order: state.preRaceScreeningPage.order
    }),
    shallowEqual
  );

  const {
    isEntriesTab,
    isEntryExamsTab,
    isEntryResultsTab,
    isRaceDayTab,
    isRaceDayResultsTab
  } = checkCurrentRaceEntriesTableTab(selectedTab.value);

  const shouldShowAssignElements = [isEntryExamsTab, isRaceDayTab].some(
    Boolean
  );

  const handleSort = (
    columnName: EPreRaceTableSortingField,
    sortDirection: SortOrder
  ) => {
    dispatch(setSortOrder(sortDirection));
    dispatch(setOrderBy(columnName));
  };

  const tableHeaderColumnsContent: ITableHeaderColumnsContent = {
    [ERowColumn.HORSE_AND_TRAINER]: [
      {
        title: "Horse",
        isDisplayed: true,
        sortableCell: {
          name: EPreRaceTableSortingField.HORSE_NAME,
          sortOrder: getSortDirection(
            EPreRaceTableSortingField.HORSE_NAME,
            orderBy,
            order
          ),
          sortHandler: handleSort
        }
      },
      {
        title: "Trainer",
        isDisplayed: true,
        highlighted: orderBy === EPreRaceTableSortingField.BARN_NAME,
        sortableCell: {
          name: EPreRaceTableSortingField.TRAINER_LAST_NAME,
          sortOrder: getSortDirection(
            EPreRaceTableSortingField.TRAINER_LAST_NAME,
            orderBy,
            order
          ),
          sortHandler: handleSort
        }
      }
    ],
    [ERowColumn.RACE_DATE]: {
      title: "Race Date",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EPreRaceTableSortingField.RACE_DATE,
        sortOrder: getSortDirection(
          EPreRaceTableSortingField.RACE_DATE,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.RACE_NUMBER]: {
      title: "Race #",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EPreRaceTableSortingField.RACE_NUMBER,
        sortOrder: getSortDirection(
          EPreRaceTableSortingField.RACE_NUMBER,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.RACE_POSITION]: {
      title: "Position #",
      isDisplayed: true,
      centered: true
    },
    [ERowColumn.RACE_TYPE]: {
      title: "Race Type",
      isDisplayed: true
    },
    [ERowColumn.WATCH_LIST]: {
      title: "Watch List",
      isDisplayed: !isEntryResultsTab,
      sortableCell: {
        name: EPreRaceTableSortingField.IS_FLAGGED,
        sortOrder: getSortDirection(
          EPreRaceTableSortingField.IS_FLAGGED,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.RISK_LEVEL]: {
      title: "Risk Level",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EPreRaceTableSortingField.RISK_LEVEL,
        sortOrder: getSortDirection(
          EPreRaceTableSortingField.RISK_LEVEL,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.BARN]: {
      title: "Barn #",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EPreRaceTableSortingField.BARN_NAME,
        sortOrder: getSortDirection(
          EPreRaceTableSortingField.BARN_NAME,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.STALL]: {
      title: "Stall #",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EPreRaceTableSortingField.STALL_NUMBER,
        sortOrder: getSortDirection(
          EPreRaceTableSortingField.STALL_NUMBER,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.CSA]: {
      title: "C.S.A",
      isDisplayed: true,
      centered: true
    },
    [ERowColumn.HSF]: {
      title: "HSF",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EPreRaceTableSortingField.HIGH_SPEED_FURLONGS,
        sortOrder: getSortDirection(
          EPreRaceTableSortingField.HIGH_SPEED_FURLONGS,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.DLW]: {
      title: "DLW",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EPreRaceTableSortingField.LAST_WORKOUT_DATE,
        sortOrder: getSortDirection(
          EPreRaceTableSortingField.LAST_WORKOUT_DATE,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.DLR]: {
      title: "DLR",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EPreRaceTableSortingField.LAST_RACE_DATE,
        sortOrder: getSortDirection(
          EPreRaceTableSortingField.LAST_RACE_DATE,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.LAST_EXAM]: {
      title: "Last Exam",
      isDisplayed: !isEntryResultsTab,
      centered: true,
      sortableCell: {
        name: EPreRaceTableSortingField.LAST_EXAM_DATE,
        sortOrder: getSortDirection(
          EPreRaceTableSortingField.LAST_EXAM_DATE,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.ACTIVE_VET_LISTS]: {
      title: "Active VL",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EPreRaceTableSortingField.ACTIVE_VET_LISTS_COUNT,
        sortOrder: getSortDirection(
          EPreRaceTableSortingField.ACTIVE_VET_LISTS_COUNT,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.RISK_FACTOR_SCORE]: {
      title: matchesDownSx ? "Risk Score" : "Risk Factor Score",
      isDisplayed: !isEntryResultsTab,
      centered: true,
      sortableCell: {
        name: EPreRaceTableSortingField.RISK_FACTOR_SCORE,
        sortOrder: getSortDirection(
          EPreRaceTableSortingField.RISK_FACTOR_SCORE,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.RISK_FACTORS]: {
      title: "Risks",
      isDisplayed: !isEntryResultsTab,
      sortableCell: {
        name: EPreRaceTableSortingField.RISK_FACTORS_COUNT,
        sortOrder: getSortDirection(
          EPreRaceTableSortingField.RISK_FACTORS_COUNT,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.ASSIGNED_VET]: {
      title: "Assigned Vet",
      isDisplayed: shouldShowAssignElements
    },
    [ERowColumn.UNASSIGN]: {
      title: "Un-Assign",
      isDisplayed: shouldShowAssignElements,
      centered: true
    },
    [ERowColumn.EXAM]: {
      title: "Exam",
      isDisplayed: shouldShowAssignElements,
      centered: true
    },
    [ERowColumn.ENTRY_REVIEW]: {
      title: "Entry Review",
      isDisplayed: isEntryResultsTab || isRaceDayTab
    },
    [ERowColumn.RACE_DAY_REVIEW]: {
      title: "Race Day Review",
      isDisplayed: isRaceDayResultsTab
    },
    [ERowColumn.EXTENDER]: {
      title: " ",
      isDisplayed: true,
      lastTwoColumnsCell: isEntriesTab,
      lastOneColumnCell: shouldShowAssignElements
    }
  };

  return (
    <>
      {isEntriesTab && (
        <ActionColumnHeaders
          columns={ENTRIES_TAB_ACTION_COLUMNS}
          refElement={refElement}
        />
      )}
      {shouldShowAssignElements && (
        <ActionColumnHeaders
          columns={ASSIGN_ACTION_COLUMNS}
          refElement={refElement}
        />
      )}

      <TableHeader
        tabClassName={columnsClassName}
        columns={columns}
        columnsContent={tableHeaderColumnsContent}
      />
    </>
  );
};

export default React.memo(PreRacesTableHeader);
